/**
 * Pure board arithmetic, kept away from the component that renders it.
 *
 * The server owns the real numbers, so nothing here recounts a column from the
 * cards on screen: a column can be showing 20 of 200 leads. Instead a move is
 * applied as a delta, which keeps the header totals honest without another
 * round trip.
 */

/** Which column currently holds this lead, if any. */
export const columnHolding = (columns, quoteRef) =>
  (columns || []).find((column) => column.cards.some((card) => card.quote_ref === quoteRef));

/** Re-derive the at-a-glance figures for one column from its own cards. */
const withTotals = (column, cards, count) => ({
  ...column,
  cards,
  count,
  shown: cards.length,
  gone_quiet: cards.filter((card) => card.gone_quiet).length,
  action_overdue: cards.filter((card) => card.next_action_overdue).length,
  value: cards.reduce((sum, card) => sum + Number(card.quote_total || 0), 0),
});

/** Roll the open columns up into the numbers along the top of the board. */
const summarise = (columns, previous) => {
  const open = columns.filter((column) => column.open);
  return {
    ...previous,
    open: open.reduce((sum, column) => sum + column.count, 0),
    gone_quiet: open.reduce((sum, column) => sum + column.gone_quiet, 0),
    action_overdue: open.reduce((sum, column) => sum + column.action_overdue, 0),
    won_value: columns.find((column) => column.key === "won")?.value ?? previous?.won_value,
  };
};

/**
 * Put an updated lead back on the board, moving it between columns if its
 * stage changed and nudging both columns' counts by one.
 */
export function mergeLead(board, updated) {
  if (!board) return board;
  const previous = columnHolding(board.columns, updated.quote_ref);
  const moved = Boolean(previous && previous.key !== updated.status);

  const columns = board.columns.map((column) => {
    let cards = column.cards.filter((card) => card.quote_ref !== updated.quote_ref);
    if (column.key === updated.status) cards = [updated, ...cards];

    let count = column.count;
    if (moved && column.key === previous.key) count = Math.max(0, count - 1);
    if (moved && column.key === updated.status) count += 1;

    return withTotals(column, cards, count);
  });

  return { ...board, columns, summary: summarise(columns, board.summary) };
}

/**
 * Whether this change needs the server's own totals again.
 *
 * A move between columns or anything that closes a lead changes the closing
 * breakdown, and that is computed across every closed lead - not just the ones
 * on screen - so it can only come from the backend.
 */
export function needsRefresh(board, updated) {
  const previous = columnHolding(board?.columns, updated.quote_ref);
  const moved = Boolean(previous && previous.key !== updated.status);
  return moved || !updated.is_open;
}
